/**
 * A MIDI keyboard played into a score: which keys are down, and what each one writes.
 *
 * Step entry from a keyboard turns on one rule, and the rule is about what is *held* rather
 * than what is struck. A key pressed with nothing else down writes a new note; a key pressed
 * while another is still down joins the note just written, which is how a chord is played in
 * rather than typed in. Letting go says nothing at all: the length of a note comes from the
 * value chosen on the screen, because this is step entry and not recording, and a player
 * who had to hold a minim for its full length would be recording.
 *
 * How hard the key was struck says nothing either. A dynamic is a mark on the page that a
 * whole passage reads from, not a property of one note, and writing one per key is how a
 * part ends up with a hairpin under every bar.
 *
 * It is a plain function over the bytes so that the rule can be read and checked without a
 * browser anywhere near it: a host reads `data` off a Web MIDI port and hands it here.
 */

import type { EntryInput } from "../editor/entry";

/** The keys of one keyboard that are down, in the order they went down. */
export interface HeldKeys {
  readonly notes: readonly number[];
}

/** Nothing held: the state a keyboard is listened to from. */
export const NOTHING_HELD: HeldKeys = { notes: [] };

/** The one entry input a keyboard makes: a note played, and whether it joins the chord. */
export type PlayedInput = Extract<EntryInput, { kind: "played" }>;

/** What one MIDI message came to: the keys now down, and the note to write, if any. */
export interface KeyPress {
  readonly held: HeldKeys;
  /** What to hand the entry machine, or `null` for a message that writes nothing. */
  readonly played: PlayedInput | null;
}

/** Channel voice messages, with the channel taken off. */
const NOTE_OFF = 0x80;
const NOTE_ON = 0x90;
const CONTROL = 0xb0;
/** "All sound off" and "all notes off": everything is up, whatever was reported before. */
const ALL_SOUND_OFF = 120;
const ALL_NOTES_OFF = 123;

function without(held: HeldKeys, note: number): HeldKeys {
  return { notes: held.notes.filter((down) => down !== note) };
}

/**
 * One MIDI message against the keys already down.
 *
 * A note-on with no velocity is a note-off; most keyboards send those and a few send both,
 * so the two are read the same way. Anything else -- the clock, the wheels, the pedal, a
 * message from a channel this is not listening on -- leaves the keys as they are and writes
 * nothing, which is what keeps a keyboard that talks a lot from filling a bar.
 */
export function pressed(held: HeldKeys, data: ArrayLike<number>): KeyPress {
  // A message shorter than it should be is read as zeroes rather than refused: a port that
  // hands over a truncated message is a broken port, and nothing here writes a note for one.
  const status = (data[0] ?? 0) & 0xf0;
  const first = data[1] ?? 0;
  const second = data[2] ?? 0;

  if (status === CONTROL && (first === ALL_SOUND_OFF || first === ALL_NOTES_OFF)) {
    return { held: NOTHING_HELD, played: null };
  }
  if (status === NOTE_OFF || (status === NOTE_ON && second === 0)) {
    return { held: without(held, first), played: null };
  }
  if (status !== NOTE_ON) return { held, played: null };

  // A key that is already down and struck again is one key, not two: a keyboard that repeats
  // a note-on would otherwise add the same head to the chord over and over.
  if (held.notes.includes(first)) return { held, played: null };
  return {
    held: { notes: [...held.notes, first] },
    played: { kind: "played", note: first, chord: held.notes.length > 0 },
  };
}
